import { AlertCircle, Bike, Calendar, Clock, MapPin, Package, Receipt } from 'lucide-react';
import './ResumenReserva.css';

const ResumenReserva = ({
  formData,
  availableVehicles,
  accessories,
  accessoriesByModel,
  calculateDuration,
  calculateVehiclePrice,
  calculateAccessoryPrice,
  calculateTotal,
  formatPrice,
  formatDate,
  t,
  language = 'es',
  showInsuranceWarnings = true
}) => {
  const durationInDays = calculateDuration();
  
  // Calcular totales
  let vehiclesTotal = 0;
  let accessoriesTotal = 0;
  
  // Calcular total de vehículos
  formData.selectedVehicles.forEach(vehicle => {
    const vehicleModel = availableVehicles.find(v => v.id === vehicle.modelId);
    if (vehicleModel) {
      vehiclesTotal += calculateVehiclePrice(vehicleModel, durationInDays);
    }
  });
  
  // Calcular total de accesorios y seguros (categoría INSURANCE) por separado
  let insuranceTotal = 0;
  Object.entries(formData.selectedAccessories).forEach(([vehicleId, accessoryIds]) => {
    const selectedVehicle = formData.selectedVehicles.find(v => v.vehicleId === parseInt(vehicleId));
    if (selectedVehicle) {
      const modelAccessories = accessoriesByModel[selectedVehicle.modelId] || [];
      accessoryIds.forEach(accessoryId => {
        let accessory = modelAccessories.find(a => a.id === accessoryId);
        if (!accessory) {
          accessory = accessories.find(a => a.id === accessoryId);
        }
        if (accessory) {
          const price = calculateAccessoryPrice(accessory);
          accessoriesTotal += price;
          if (accessory.category === 'INSURANCE') {
            insuranceTotal += price;
          }
        }
      });
    }
  });
  const accessoriesOnlyTotal = accessoriesTotal - insuranceTotal;
  
  const deliveryCost = formData.deliveryType === 'DELIVERY' && formData.deliveryCost 
    ? formData.deliveryCost 
    : 0;
  
  const total = calculateTotal();

  // Avisos de seguro (misma lógica que booking): una bicicleta = al menos un seguro; si hay varias, todas con seguro
  const vehiclesWithInsurance = formData.selectedVehicles.filter((vehicle) => {
    const vehicleAccessoryIds = formData.selectedAccessories[vehicle.vehicleId] || [];
    if (vehicleAccessoryIds.length === 0) return false;
    const modelAccessories = accessoriesByModel[vehicle.modelId] || [];
    return vehicleAccessoryIds.some(
      (accId) => modelAccessories.find((a) => a.id === accId)?.category === 'INSURANCE'
    );
  }).length;
  const totalVehicles = formData.selectedVehicles.length;
  const showNoInsuranceWarning =
    showInsuranceWarnings && totalVehicles >= 1 && vehiclesWithInsurance === 0;
  const showPartialInsuranceWarning =
    showInsuranceWarnings &&
    totalVehicles > 1 &&
    vehiclesWithInsurance > 0 &&
    vehiclesWithInsurance < totalVehicles;

  return (
    <div className="resumen-reserva">
      <div className="resumen-header">
        <Receipt size={18} />
        <h3>{t.priceBreakdown || 'Desglose de precios'}</h3>
      </div>

      {/* Avisos de seguro */}
      {showNoInsuranceWarning && (
        <div className="resumen-insurance-warning">
          <div className="resumen-insurance-warning-inner">
            <AlertCircle size={18} className="resumen-insurance-warning-icon" />
            <span className="resumen-insurance-warning-text">{t.warningInsuranceNone}</span>
          </div>
        </div>
      )}
      {showPartialInsuranceWarning && (
        <div className="resumen-insurance-warning">
          <div className="resumen-insurance-warning-inner">
            <AlertCircle size={18} className="resumen-insurance-warning-icon" />
            <span className="resumen-insurance-warning-text">
              {vehiclesWithInsurance === 1
                ? t.warningInsuranceOnlyOneBike
                : (t.warningInsurancePartial || '').replace('{count}', vehiclesWithInsurance)}
            </span>
          </div>
        </div>
      )}

      {/* Fechas y duración */}
      {formData.startDate && formData.endDate && (
        <div className="resumen-section">
          <div className="resumen-dates">
            <div className="resumen-date-item">
              <Calendar size={14} />
              <span className="resumen-date-label">{t.startDate}:</span>
              <span className="resumen-date-value">
                {new Date(formData.startDate).toLocaleDateString(
                  language === 'es' ? 'es-ES' : language === 'en' ? 'en-US' : 'de-DE',
                  {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  }
                )}
              </span>
            </div>
            <div className="resumen-date-item">
              <Calendar size={14} />
              <span className="resumen-date-label">{t.endDate}:</span>
              <span className="resumen-date-value">
                {new Date(formData.endDate).toLocaleDateString(
                  language === 'es' ? 'es-ES' : language === 'en' ? 'en-US' : 'de-DE',
                  {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  }
                )}
              </span>
            </div>
            <div className="resumen-duration">
              <Clock size={14} />
              <span>{t.duration}: <strong>{durationInDays} {durationInDays === 1 ? t.day : t.days}</strong></span>
            </div>
          </div>
        </div>
      )}

      {/* Vehículos seleccionados */}
      {formData.selectedVehicles.length > 0 && (
        <div className="resumen-section">
          <div className="resumen-section-header">
            <Bike size={16} />
            <span>{t.vehicles} ({formData.selectedVehicles.length})</span>
          </div>
          <div className="resumen-items-list">
            {formData.selectedVehicles.map((vehicle, index) => {
              const vehicleModel = availableVehicles.find(v => v.id === vehicle.modelId);
              const vehiclePrice = vehicleModel ? calculateVehiclePrice(vehicleModel, durationInDays) : 0;
              
              return (
                <div key={`${vehicle.vehicleId}-${index}`} className="resumen-item">
                  <div className="resumen-item-info">
                    <span className="resumen-item-name">{vehicle.modelName}</span>
                    <span className="resumen-item-details">
                      {t.size}: {vehicle.size} • {t.serialNumber}: {vehicle.serialNumber}
                    </span>
                  </div>
                  <span className="resumen-item-price">{formatPrice(vehiclePrice)}</span>
                </div>
              );
            })}
          </div>
          <div className="resumen-subtotal">
            <span>{t.vehicles} ({durationInDays} {durationInDays === 1 ? t.day : t.days}):</span>
            <span>{formatPrice(vehiclesTotal)}</span>
          </div>
        </div>
      )}

      {/* Accesorios seleccionados */}
      {accessoriesTotal > 0 && (
        <div className="resumen-section">
          <div className="resumen-section-header">
            <Package size={16} />
            <span>{t.accessories}</span>
          </div>
          <div className="resumen-items-list">
            {Object.entries(formData.selectedAccessories).map(([vehicleId, accessoryIds]) => {
              const selectedVehicle = formData.selectedVehicles.find(v => v.vehicleId === parseInt(vehicleId));
              if (!selectedVehicle) return null;
              
              const modelAccessories = accessoriesByModel[selectedVehicle.modelId] || [];
              
              return accessoryIds.map(accessoryId => {
                let accessory = modelAccessories.find(a => a.id === accessoryId);
                if (!accessory) {
                  accessory = accessories.find(a => a.id === accessoryId);
                }
                if (!accessory) return null;
                
                const accessoryPrice = calculateAccessoryPrice(accessory);
                
                return (
                  <div key={`${vehicleId}-${accessoryId}`} className="resumen-item">
                    <div className="resumen-item-info">
                      <span className="resumen-item-name">{accessory.name}</span>
                      <span className="resumen-item-details">
                        {accessory.accessoryPriceType === 'FIXED' 
                          ? `(${t.fixedPrice || 'Fijo'})`
                          : `(${t.dailyPrice || 'Por día'})`
                        }
                        {accessory.accessoryPriceType === 'DAILY' && ` • ${durationInDays} ${durationInDays === 1 ? t.day : t.days}`}
                      </span>
                    </div>
                    <span className="resumen-item-price">{formatPrice(accessoryPrice)}</span>
                  </div>
                );
              });
            })}
          </div>
          {(accessoriesOnlyTotal > 0 || insuranceTotal > 0) && (
            <>
              {accessoriesOnlyTotal > 0 && (
                <div className="resumen-subtotal">
                  <span>{t.accessories} ({durationInDays} {durationInDays === 1 ? t.day : t.days}):</span>
                  <span>{formatPrice(accessoriesOnlyTotal)}</span>
                </div>
              )}
              {insuranceTotal > 0 && (
                <div className="resumen-subtotal">
                  <span>{t.insuranceSubtotal ?? 'Seguros'}:</span>
                  <span>{formatPrice(insuranceTotal)}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Coste de entrega */}
      {deliveryCost > 0 && (
        <div className="resumen-section">
          <div className="resumen-delivery">
            <div className="resumen-delivery-info">
              <MapPin size={16} />
              <span>{t.delivery || 'Entrega a domicilio'}:</span>
            </div>
            <span className="resumen-delivery-price">{formatPrice(deliveryCost)}</span>
          </div>
        </div>
      )}

      {/* Total */}
      <div className="resumen-total">
        <div className="resumen-total-label-group">
          <span className="resumen-total-label">{t.total}:</span>
          <span className="resumen-vat-included">{t.vatIncluded || 'IVA incluido'}</span>
        </div>
        <span className="resumen-total-value">{formatPrice(total)}</span>
      </div>
    </div>
  );
};

export default ResumenReserva;

